/**
 * baseQuery — shared RTK Query fetch base.
 *
 * All domain API slices use this as their baseQuery. It injects the
 * Bearer token from AuthorizeService before every request and handles
 * 401 session expiry the same way the legacy Axios wrapper did.
 */

import { fetchBaseQuery, type FetchBaseQueryError, type FetchArgs } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import Swal from 'sweetalert2';
import authService from './api-authorization/AuthorizeService';
import { ApplicationPaths } from './api-authorization/ApiAuthorizationConstants';

const rawBaseQuery = fetchBaseQuery({
    baseUrl: '/',
    prepareHeaders: async (headers) => {
        const token = await authService.getAccessToken();
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
    },
});

/**
 * baseQuery wraps rawBaseQuery to intercept 401s and show the session-expired
 * dialog before propagating the error.
 */
export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status === 401) {
        if ((authService as any)._token) {
            Swal.fire({
                title: 'Session Expired',
                text: 'Your session has expired. Please login again.',
                showCancelButton: false,
                confirmButtonColor: '#5052bb',
                confirmButtonText: 'Yes',
            }).then(() => {
                window.location.href = ApplicationPaths.LogOut;
            });
        }
    }
    return result;
};
